// Module-level LRU cache for `SampledImage`s by source URL.
//
// On a typical Morfi page, the same outcome image / avatar / market cover
// can appear in many places (lists, cards, detail headers, mini-maps).
// Without caching, each `<PatternCanvas>` instance would fire its own
// fetch + decode + sample pipeline, hammering Pinata and burning CPU on
// duplicate work.
//
// The cache stores promises (not resolved values) so concurrent requests
// for the same URL coalesce into a single in-flight fetch.

import type { SampledImage } from '../../types';
import { loadImageFromUrl } from './url-loader';

const CACHE_SIZE = 32;

const cache: Map<string, Promise<SampledImage>> = new Map();

/**
 * Cached variant of `loadImageFromUrl`. Concurrent + repeat calls for
 * the same URL share a single fetch + decode + sample pipeline. Capped
 * at 32 entries (LRU); evicting an entry doesn't cancel its in-flight
 * fetch — it just stops the result from being reused.
 *
 * Failed fetches are evicted from the cache so they're retried next
 * call. (Without this, a transient network error would poison the cache
 * for that URL until the next page load.)
 */
export function cachedLoadImageFromUrl(url: string): Promise<SampledImage> {
  const existing = cache.get(url);
  if (existing !== undefined) {
    // Move to MRU position by re-inserting (Map preserves insertion order).
    cache.delete(url);
    cache.set(url, existing);
    return existing;
  }

  const promise = loadImageFromUrl(url).catch((err: unknown) => {
    // Don't cache failures — let the next caller retry.
    cache.delete(url);
    throw err;
  });

  cache.set(url, promise);

  // Evict oldest entries until under cap.
  while (cache.size > CACHE_SIZE) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }

  return promise;
}

/**
 * Clear the entire cache. Mostly for tests; production callers should
 * never need this — entries evict naturally under load.
 */
export function clearImageCache(): void {
  cache.clear();
}

/**
 * Drop a single URL from the cache. Use this if you've replaced the
 * image at that URL and want the next fetch to see the new content.
 * (Pinata content-addresses, so this is rare in practice.)
 */
export function invalidateImageCache(url: string): void {
  cache.delete(url);
}
